import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ModalComponent, Text, TableComponent } from "@iiht/components";
import { ExclamationCircleFilled } from "@ant-design/icons";
import {
  myLabsLoaderRequest,
  extensionValidityGetSuccess,
  myLabsLoaderSuccess,
  extsnVldtyUpdateSuccess,
  extensionValidityGetRequest,
  extsnVldtyUpdateError,
  extsnVldtyCreateRequest,
  extsnVldtyUpdateRequest,
  extensionValidityGetError,
  getWorkspaceExtPacks,
} from "../../store/actions";
import { checkVmExtendQuotaWorkspaceLogin } from "../../store/api/workspaceManager";
import { checkVmExtendQuota } from "../../store/api";
import { approvals, toasterFlags } from "../../constants/componentConstants";
import { statusCode } from "../../constants/apis/apiStatus";
import { appendParam, extColumns } from "../../utils";
import { toastNotification } from "../../utils/toast";
import { ExlamationErrorIcon, Warning } from "../../icons";
import "./useExtendValidityStyle.scss";

const useExtendVmValidity = ({ type, isWorkspaceDashboard }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const extcolumns = extColumns(t);
  const [quotanotAvailable, setQuotanotAvailable] = useState();
  const [extensionAvailable, setExtensionAvailable] = useState();
  const [extensionNotAvailable, setExtensionNotAvailable] = useState();
  const [extendRecord, setExtendRecord] = useState({});
  const [extendApproval, setExtendApproval] = useState("");
  const [selectedExtsnPack, setSelectedExtsnPack] = useState({});
  const [exstnPackSuccess, setExtsnPackSuccess] = useState();
  const [approvalRequiredModal, setApprovalRequiredModal] = useState(false);
  const [showAddButton, setShowAddButton] = useState(false);
  const [disableValidate, setDisableValidate] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showModalType, setShowModalType] = useState("");
  const { myLabsLoader, extensionSuccess, extensionError, getPackError, extsnUpdateSuccess } = useSelector(
    ({ myLabsReducer }) => myLabsReducer
  );

  useEffect(() => {
    if (type === showModalType) {
      if (extensionError) {
        setQuotanotAvailable(true);
        dispatch(extensionValidityGetSuccess(""));
        dispatch(myLabsLoaderSuccess(false));
      } else if (getPackError) {
        setExtensionNotAvailable(true);
        dispatch(extensionValidityGetSuccess(""));
        dispatch(myLabsLoaderSuccess(false));
      } else if (extensionSuccess) {
        dispatch(myLabsLoaderSuccess(false));
        setExtensionAvailable(true);
      } else if (extsnUpdateSuccess) {
        setExtsnPackSuccess(true);
        dispatch(extensionValidityGetSuccess(""));
        dispatch(extsnVldtyUpdateSuccess(""));
        dispatch(myLabsLoaderSuccess(false));
      }
      setLoading(false);
    }
  }, [dispatch, extensionError, extensionSuccess, extsnUpdateSuccess, getPackError]);

  const handleExtendValidity = async (record, modalType) => {
    setShowModalType(modalType);
    setShowAddButton(false);
    setExtendRecord(record);
    setDisableValidate(true);
    const reqBody = {
      params: { tenant: record?.tenant ? record?.tenant : record?.workspace_id, vm_id: record?.id },
    };
    dispatch(extensionValidityGetRequest(reqBody));
  };

  const handleExtendValidityWorkspace = async (record, modalType) => {
    setShowModalType(modalType);
    setShowAddButton(false);
    setExtendRecord(record);
    setDisableValidate(true);
    let params = appendParam(record?.workspace_id, "vm_id", record?.id);
    dispatch(getWorkspaceExtPacks(params));
  };

  const validateExtension = async () => {
    const record = extendRecord;
    let params = appendParam("", "tenantid", record?.tenant);
    params = appendParam(params, "userid", record?.user);
    params = appendParam(params, "vmid", record?.id);
    params = appendParam(params, "duration_minutes", selectedExtsnPack?.[0]?.duration_minutes);
    setLoading(true);
    if (isWorkspaceDashboard) {
      params = appendParam(params, "workspace_id", record?.workspace_id);
    }
    const extendCheck = isWorkspaceDashboard ? await checkVmExtendQuotaWorkspaceLogin(params) : await checkVmExtendQuota(params);
    setExtendApproval((prevState) => "");
    if (extendCheck?.status === statusCode.Ok200) {
      setExtendApproval((prevState) => "");
      setShowAddButton(true);
      setLoading(false);
    } else if (extendCheck?.status === statusCode.BadRequest400 && extendCheck?.data?.type === approvals.extnsApproval) {
      setExtendApproval((prevState) => approvals.extnsApproval);
      setShowAddButton(true);
      setLoading(false);
    } else if (extendCheck?.status === statusCode.BadRequest400 && extendCheck?.data?.type === approvals.quotaExceed) {
      setExtensionAvailable(false);
      dispatch(extsnVldtyUpdateError(extendCheck?.data?.message));
      setLoading(false);
    } else {
      setExtensionAvailable(false);
      toastNotification(toasterFlags.WARN, extendCheck?.data?.message);
      setLoading(false);
    }
  };

  const handleExtensionCancel = () => {
    dispatch(extensionValidityGetSuccess(""));
    setExtensionAvailable(false);
    setExtendApproval("");
  };

  const handleExtensionModal = () => {
    dispatch(extensionValidityGetSuccess(""));
    setShowAddButton(false);
    if (extendApproval === approvals.extnsApproval) {
      setExtensionAvailable(false);
      setApprovalRequiredModal(true);
    } else {
      handleExtensionOk();
    }
  };

  const handleExtensionOk = () => {
    setExtensionAvailable(false);
    let extsnData = {
      id: extendRecord?.id,
      tenant: extendRecord?.tenant,
      user: extendRecord?.user,
      extension_id: selectedExtsnPack?.[0]?.id,
      extension_name: selectedExtsnPack?.[0]?.name,
      validity_days: selectedExtsnPack?.[0]?.validity_days,
      duration_minutes: selectedExtsnPack?.[0]?.duration_minutes,
      flag: true,
    };
    dispatch(myLabsLoaderRequest(true));
    if (extendApproval === approvals.extnsApproval) {
      dispatch(extsnVldtyCreateRequest(extsnData));
    } else {
      dispatch(extsnVldtyUpdateRequest(extsnData));
    }
    dispatch(extensionValidityGetSuccess(""));
  };

  const handleExtnsNotCancel = () => {
    setExtensionNotAvailable(false);
    dispatch(extsnVldtyUpdateError(""));
    dispatch(extensionValidityGetError(""));
  };

  const handleQotaCancel = () => {
    setQuotanotAvailable(false);
    dispatch(extsnVldtyUpdateError(""));
    dispatch(extensionValidityGetError(""));
  };

  const handleExtnsUpdateCancel = () => {
    dispatch(extsnVldtyUpdateError(""));
    dispatch(extsnVldtyUpdateSuccess(""));
    dispatch(extensionValidityGetSuccess(""));
    dispatch(extensionValidityGetError(""));
    setExtsnPackSuccess(false);
  };

  const onChangeCheckbox = (selectedRowKeys, selectedRows) => {
    setShowAddButton(false);
    setSelectedExtsnPack(selectedRows);
    setDisableValidate(false);
  };

  const handleApprovalOk = () => {
    setLoading(true);
    setApprovalRequiredModal(false);
    handleExtensionOk();
  };

  const handleApprovalCancel = () => {
    setApprovalRequiredModal(false);
    setExtendApproval("");
  };

  const extendValidityDom = (
    <>
      {quotanotAvailable && (
        <ModalComponent bodyCls="text-align-center" visible={quotanotAvailable} handleCancel={handleQotaCancel} closable>
          <ModalComponent.Icon>
            <ExlamationErrorIcon />
          </ModalComponent.Icon>
          <ModalComponent.Text className="text-align-center">
            <Text className="sub-heading-text-two font-wt-bold">{`${extensionError}`}</Text>
          </ModalComponent.Text>
        </ModalComponent>
      )}

      {extensionNotAvailable && (
        <ModalComponent bodyCls="text-align-center" visible={extensionNotAvailable} handleCancel={handleExtnsNotCancel} closable>
          <ModalComponent.Icon>
            <ExlamationErrorIcon />
          </ModalComponent.Icon>
          <ModalComponent.Text>
            <Text className="sub-heading-text-two font-wt-bold">{`${getPackError}`}</Text>
          </ModalComponent.Text>
        </ModalComponent>
      )}

      {exstnPackSuccess && (
        <ModalComponent visible={exstnPackSuccess} handleCancel={handleExtnsUpdateCancel} closable>
          <ModalComponent.Icon>
            <ExclamationCircleFilled />
          </ModalComponent.Icon>
          <ModalComponent.Text>
            <Text className="sub-heading-text-two font-wt-bold">
              {t(extendApproval === approvals.extnsApproval ? "myLabs.extensionUpdateRequestSent" : "myLabs.extensUpdateSuccess")}
            </Text>
          </ModalComponent.Text>
        </ModalComponent>
      )}

      {extensionAvailable && (
        <ModalComponent
          visible={extensionAvailable}
          handleOk={handleExtensionModal}
          handleCancel={handleExtensionCancel}
          closable
          logo={<ExclamationCircleFilled />}
        >
          <ModalComponent.Text>
            <Text className="sub-heading-text-three font-wt-bold">{t("myLabs.selectPack")}</Text>
          </ModalComponent.Text>
          <TableComponent
            columns={extcolumns}
            rows={extensionSuccess?.data}
            total={extensionSuccess?.data?.length}
            defaultPageSize={10}
            paginationDisabled
            id="id"
            size="small"
            rowSelectionEnabled
            selectionType="radio"
            className="myLabs-extend-table qa-mylabs-extend-table"
            onChangeCheckbox={onChangeCheckbox}
            scrollY="10rem"
          />
          <div className="modal-btn-wrapper">
            <ModalComponent.Cancel className="qa-add-extension-cancel" onClick={handleExtensionCancel}>
              {t("common.cancelText")}
            </ModalComponent.Cancel>
            {!showAddButton && (
              <ModalComponent.Proceed
                className="qa-add-extension-ok"
                onClick={validateExtension}
                loading={loading}
                disabled={disableValidate}
              >
                {t("validate")}
              </ModalComponent.Proceed>
            )}
            {showAddButton && (
              <ModalComponent.Proceed className="qa-add-extension-ok" onClick={handleExtensionModal} loading={myLabsLoader}>
                {t("common.addText")}
              </ModalComponent.Proceed>
            )}
          </div>
        </ModalComponent>
      )}
      {approvalRequiredModal && (
        <ModalComponent visible={approvalRequiredModal} handleOk={handleApprovalOk} handleCancel={handleApprovalCancel} closable>
          <ModalComponent.Icon>
            <Warning />
          </ModalComponent.Icon>
          <ModalComponent.H1>
            <Text className="sub-heading-text-two" strong>
              {t("common.approvalRequired")}
            </Text>
          </ModalComponent.H1>
          <ModalComponent.Text>
            <Text className="body-short-text" strong>
              {`${t("myLabs.approvalSent")} ${extendRecord?.vm_name}`}
            </Text>
          </ModalComponent.Text>
          <ModalComponent.Text>
            <Text className="body-short-text" strong>
              {t("common.wantToContinue")}
            </Text>
          </ModalComponent.Text>
          <div className="modal-btn-wrapper">
            <ModalComponent.Cancel onClick={handleApprovalCancel}>
              <Text className="body-short-text" strong>
                {t("common.cancelText")}
              </Text>
            </ModalComponent.Cancel>
            <ModalComponent.Proceed onClick={handleApprovalOk}>
              <Text className="body-short-text text-white" strong>
                {t("userMachineCatalog.requestApproval")}
              </Text>
            </ModalComponent.Proceed>
          </div>
        </ModalComponent>
      )}
    </>
  );

  return { handleExtendValidity, extendValidityDom, handleExtendValidityWorkspace, handleExtnsUpdateCancel };
};

export default useExtendVmValidity;
